import { CartUpdateEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * <dvm-buy-box> wraps the pack radio rows, quantity stepper, add-to-cart
 * button, and free-shipping nudge on the Diamond View Mounts product page.
 * Prices always come from the selected variant's live price — never
 * hardcoded — per the README's "don't hardcode prices" note.
 */
class DvmBuyBox extends HTMLElement {
  connectedCallback() {
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';
    this.threshold = parseInt(this.dataset.freeShippingThreshold || '3500', 10);
    this.cartAddUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

    this.radios = Array.from(this.querySelectorAll('[data-dvm-pack-radio]'));
    this.rows = Array.from(this.querySelectorAll('[data-dvm-pack-row]'));
    this.qtyInput = this.querySelector('[data-dvm-qty-value]');
    this.qtyDec = this.querySelector('[data-dvm-qty-dec]');
    this.qtyInc = this.querySelector('[data-dvm-qty-inc]');
    this.totalEl = this.querySelector('[data-dvm-total]');
    this.submitBtn = this.querySelector('[data-dvm-submit]');
    this.nudgeEl = this.querySelector('[data-dvm-nudge]');
    this.form = this.querySelector('form');

    this.qty = parseInt((this.qtyInput && this.qtyInput.value) || '1', 10) || 1;

    this.radios.forEach((radio) => {
      radio.addEventListener('change', () => this.update());
      radio.closest('[data-dvm-pack-row]')?.addEventListener('click', (event) => {
        if (event.target.closest('[data-dvm-pack-radio]')) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    this.qtyDec?.addEventListener('click', () => this.changeQty(-1));
    this.qtyInc?.addEventListener('click', () => this.changeQty(1));

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.addToCart();
    });

    this.update();
  }

  get selectedRadio() {
    return this.radios.find((radio) => radio.checked) || this.radios[0];
  }

  changeQty(delta) {
    this.qty = Math.min(9, Math.max(1, this.qty + delta));
    if (this.qtyInput) this.qtyInput.textContent = this.qty;
    this.update();
  }

  formatCents(cents) {
    return formatMoney(cents, this.moneyFormat, this.currency);
  }

  update() {
    const selected = this.selectedRadio;
    if (!selected) return;

    this.rows.forEach((row) => {
      const radio = row.querySelector('[data-dvm-pack-radio]');
      row.classList.toggle('is-selected', radio === selected);
    });

    const price = parseInt(selected.dataset.price || '0', 10);
    const total = price * this.qty;

    if (this.totalEl) {
      this.totalEl.textContent = this.formatCents(total);
    }

    if (this.submitBtn) {
      this.submitBtn.disabled = selected.dataset.available === 'false';
    }

    if (this.nudgeEl) {
      const freeShipText = this.nudgeEl.dataset.freeText || "This order ships free";
      const almostTemplate = this.nudgeEl.dataset.almostText || "You're {amount} away from free shipping.";
      if (total >= this.threshold) {
        this.nudgeEl.classList.add('dvm-nudge--free');
        this.nudgeEl.classList.remove('dvm-nudge--almost');
        this.nudgeEl.innerHTML = '&#10003; ' + freeShipText;
      } else {
        this.nudgeEl.classList.add('dvm-nudge--almost');
        this.nudgeEl.classList.remove('dvm-nudge--free');
        const remaining = this.formatCents(this.threshold - total);
        this.nudgeEl.textContent = almostTemplate.replace('{amount}', remaining);
      }
    }
  }

  async addToCart() {
    const selected = this.selectedRadio;
    if (!selected || selected.dataset.available === 'false' || !this.submitBtn) return;

    const variantId = selected.value;
    const originalLabel = this.submitBtn.innerHTML;
    this.submitBtn.disabled = true;
    this.submitBtn.classList.add('is-loading');

    try {
      const response = await fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: this.qty }] }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Could not add to cart');
      }

      document.dispatchEvent(
        new CartUpdateEvent({ item_count: this.qty }, this.id || 'dvm-buy-box', {
          itemCount: this.qty,
          source: 'product-form-component',
          variantId: String(variantId),
        })
      );

      this.submitBtn.innerHTML = 'Added &#10003;';
      window.setTimeout(() => {
        this.submitBtn.innerHTML = originalLabel;
        this.submitBtn.disabled = false;
        this.submitBtn.classList.remove('is-loading');
      }, 1800);
    } catch (error) {
      this.submitBtn.innerHTML = originalLabel;
      this.submitBtn.disabled = false;
      this.submitBtn.classList.remove('is-loading');
      if (this.nudgeEl) {
        this.nudgeEl.textContent = 'Something went wrong adding this to your cart — please try again.';
        this.nudgeEl.classList.remove('dvm-nudge--free', 'dvm-nudge--almost');
        this.nudgeEl.classList.add('dvm-nudge--error');
      }
    }
  }
}

if (!customElements.get('dvm-buy-box')) {
  customElements.define('dvm-buy-box', DvmBuyBox);
}

/**
 * <dvm-gallery> swaps the main product image when a thumbnail is clicked.
 */
class DvmGallery extends HTMLElement {
  connectedCallback() {
    this.main = this.querySelector('[data-dvm-gallery-main]');
    this.thumbs = Array.from(this.querySelectorAll('[data-dvm-gallery-thumb]'));

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        if (!this.main) return;
        const fullSrc = thumb.dataset.fullSrc;
        const alt = thumb.dataset.fullAlt || '';
        if (!fullSrc) return;
        this.main.src = fullSrc;
        this.main.alt = alt;
        this.thumbs.forEach((t) => t.classList.toggle('is-active', t === thumb));
      });
    });
  }
}

if (!customElements.get('dvm-gallery')) {
  customElements.define('dvm-gallery', DvmGallery);
}
